// DeskBriefs — the brief on the Studio desk (pen "Studio – the desk",
// decisions 2 and 3):
//  - PlanRow: "Plan a document – title and what it should contain" at the
//    end of an open binder (or "Plan a module" on a shelf), unfolding in
//    place into a short form;
//  - BriefBox: an open document's brief, Edit while it is planned, read-only
//    with a short note once generated; Generate while planned;
//  - ReadinessLine: "Before export: …" at an open container's foot, with
//    "Generate it" while documents are planned;
//  - NoteLine: what came of a generate, in place (deskFacts.DeskNote);
//  - WritingMiddle / PlannedMiddle: an open document being written (the B2
//    skeleton) or not written yet.
// Pure: facts and notes are projected in; each change goes out as an intent.
// Local state is an unsent draft only (the form's fields, the brief edit).

export { BriefBox, PlannedMiddle, PlanRow, ReadinessLine, WritingMiddle };

import { exhaustSwitchCase } from "@bilda/utils";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { type KeyboardEvent, type ReactNode, useState } from "react";
import type { ExportReadiness } from "../../app/ports/brief.js";
import { Button } from "../Button/Button.js";
import { Icon } from "../Icon/Icon.js";
import { Input } from "../Input/Input.js";
import { Skeleton } from "../Skeleton/Skeleton.js";
import { Textarea } from "../Textarea/Textarea.js";
import {
    briefEditable,
    type DeskNote,
    type DocState,
    type ReadFailure,
    type ReadinessItem,
    readinessItems,
} from "./deskFacts.js";

// Asked, or reading the sources: Generate waits.
const busy = (note: DeskNote | undefined): boolean =>
    note?.type === "starting" || note?.type === "reading";

// Nothing was written; each reason names the way forward (the material
// side view's words, StatusLine.tsx).
const ReadFailedText = ({
    reason,
}: {
    readonly reason: ReadFailure;
}): string => {
    const { t } = useLingui();
    switch (reason) {
        case "sources_unreadable":
            return t`Nothing written: the sources have no readable text yet. Wait until they are read in, or add a source with text, then generate again.`;
        case "no_claims_found":
            return t`Nothing written: no facts could be read from the sources. Add a source that covers this material, then generate again.`;
        case "reading_failed":
        case "unknown":
            return t`Nothing written: reading the sources failed. Generate again to retry.`;
        default:
            return exhaustSwitchCase(reason);
    }
};

// Esc inside a form cancels it (and so does not close the panel).
const cancelOnEsc =
    (cancel: () => void) =>
    (e: KeyboardEvent<HTMLElement>): void => {
        if (e.key !== "Escape") return;
        e.preventDefault();
        cancel();
    };

const PlanRow = ({
    what,
    online,
    onPlan,
}: {
    // A document (title and brief) or a module (title).
    readonly what: "document" | "module";
    readonly online: boolean;
    readonly onPlan: (title: string, brief: string) => void;
}): ReactNode => {
    const { t } = useLingui();
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState("");
    const [brief, setBrief] = useState("");
    const close = () => {
        setOpen(false);
        setTitle("");
        setBrief("");
    };
    const ready =
        online &&
        title.trim() !== "" &&
        (what === "module" || brief.trim() !== "");
    if (!open)
        return (
            <button
                type="button"
                className="bilda-desk__plan"
                disabled={!online}
                onClick={() => setOpen(true)}
            >
                <Icon name="plus" size="sm" />
                {what === "document"
                    ? t`Plan a document – title and what it should contain`
                    : t`Plan a module`}
            </button>
        );
    return (
        <form
            className="bilda-desk__plan-form"
            onKeyDown={cancelOnEsc(close)}
            onSubmit={(e) => {
                e.preventDefault();
                if (!ready) return;
                onPlan(title.trim(), brief.trim());
                close();
            }}
        >
            <Input
                size="sm"
                autoFocus
                aria-label={t`Title`}
                placeholder={
                    what === "document" ? t`Document title` : t`Module title`
                }
                value={title}
                onChange={(e) => setTitle(e.target.value)}
            />
            {what === "document" ? (
                <Textarea
                    size="sm"
                    rows={2}
                    aria-label={t`What it should contain`}
                    placeholder={t`What it should contain`}
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                />
            ) : null}
            <span className="bilda-desk__plan-actions">
                <Button type="button" variant="ghost" size="sm" onClick={close}>
                    {t`Cancel`}
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    size="sm"
                    disabled={!ready}
                >
                    {t`Plan`}
                </Button>
            </span>
        </form>
    );
};

const NoteLine = ({ note }: { readonly note: DeskNote }): ReactNode => {
    const { t } = useLingui();
    const text = ((): ReactNode => {
        switch (note.type) {
            case "starting":
                return t`Starting …`;
            case "reading":
                return t`Reading the sources …`;
            case "read_failed":
                return <ReadFailedText reason={note.reason} />;
            case "no_sources":
                return t`Add a source to this module first`;
            case "already_writing":
                return t`This is being written already`;
            case "brief_locked":
                return t`Generated already: the brief stays as it is for now`;
            case "failed":
                return note.text;
            default:
                return exhaustSwitchCase(note);
        }
    })();
    return (
        <p className="bilda-desk__note" data-note={note.type} role="status">
            <Icon
                name={
                    note.type === "starting" || note.type === "reading"
                        ? "sparkle"
                        : "info"
                }
                size="sm"
            />
            {text}
        </p>
    );
};

const BriefBox = ({
    brief,
    state,
    note,
    online,
    onRevise,
    onGenerate,
}: {
    readonly brief: string | undefined;
    readonly state: DocState;
    readonly note: DeskNote | undefined;
    readonly online: boolean;
    readonly onRevise: (brief: string) => void;
    readonly onGenerate: () => void;
}): ReactNode => {
    const { t } = useLingui();
    const [draft, setDraft] = useState<string | null>(null);
    const editable = briefEditable(state);
    const planned = state === "planned" || state === "unknown";
    if (brief === undefined && !planned) return null;
    const save = () => {
        if (draft !== null && draft.trim() !== "" && draft !== brief)
            onRevise(draft.trim());
        setDraft(null);
    };
    return (
        <section className="bilda-desk__brief-box">
            <div className="bilda-desk__brief-head">
                <p className="bilda-desk__brief-label">
                    {t`Brief – what this should contain`}
                </p>
                {editable && draft === null ? (
                    <button
                        type="button"
                        className="bilda-desk__brief-edit"
                        disabled={!online}
                        onClick={() => setDraft(brief ?? "")}
                    >
                        {t`Edit`}
                    </button>
                ) : null}
            </div>
            {draft === null ? (
                <p className="bilda-desk__brief-text">{brief}</p>
            ) : (
                <form
                    className="bilda-desk__plan-form"
                    onKeyDown={cancelOnEsc(() => setDraft(null))}
                    onSubmit={(e) => {
                        e.preventDefault();
                        save();
                    }}
                >
                    <Textarea
                        size="sm"
                        autoFocus
                        aria-label={t`Brief – what this should contain`}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                    />
                    <span className="bilda-desk__plan-actions">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setDraft(null)}
                        >
                            {t`Cancel`}
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            disabled={!online || draft.trim() === ""}
                        >
                            {t`Save`}
                        </Button>
                    </span>
                </form>
            )}
            {state !== "generated" || brief === undefined ? null : (
                <p className="bilda-desk__brief-after">
                    {t`Generated from this brief. A changed brief will give a new draft to accept or reject; that comes later.`}
                </p>
            )}
            {planned && draft === null ? (
                <div className="bilda-desk__brief-foot">
                    <Button
                        variant="primary"
                        size="sm"
                        disabled={!online || busy(note)}
                        onClick={onGenerate}
                    >
                        <Icon name="sparkle" size="sm" />
                        {t`Generate`}
                    </Button>
                </div>
            ) : null}
            {/* Reading shows in the middle, as its step line. */}
            {note === undefined || note.type === "reading" ? null : (
                <NoteLine note={note} />
            )}
        </section>
    );
};

const ItemText = ({ item }: { readonly item: ReadinessItem }): string => {
    const { t } = useLingui();
    switch (item.type) {
        case "planned":
            return t`${plural(item.count, {
                one: "# document planned, not written",
                other: "# documents planned, not written",
            })}`;
        case "empty":
            return t`${plural(item.count, {
                one: "# document empty",
                other: "# documents empty",
            })}`;
        case "removed":
            return t`${plural(item.count, {
                one: "# document removed elsewhere",
                other: "# documents removed elsewhere",
            })}`;
        case "moved": {
            const { target } = item;
            return t`${plural(item.count, {
                one: `# part changes place in ${target}`,
                other: `# parts change place in ${target}`,
            })}`;
        }
        case "unavailable": {
            const { target } = item;
            return t`${target} cannot take it`;
        }
        case "loss": {
            const { target } = item;
            switch (item.loss) {
                case "lost_interactivity":
                    return t`interactive parts become text in ${target}`;
                case "lost_objective_alignment":
                    return t`goals are left out in ${target}`;
                case "lost_grading_logic":
                    return t`grading is left out in ${target}`;
                case "lost_styling":
                    return t`styling is simplified in ${target}`;
                case "lost_audio_metadata":
                    return t`audio details are left out in ${target}`;
                case "other":
                    return t`some things change in ${target}`;
                default:
                    return exhaustSwitchCase(item.loss);
            }
        }
        default:
            return exhaustSwitchCase(item);
    }
};

const ReadinessLine = ({
    readiness,
    planned,
    note,
    online,
    onGenerateAll,
}: {
    readonly readiness: ExportReadiness | undefined;
    // What "Generate it" writes (deskFacts.toGenerate).
    readonly planned: readonly string[];
    readonly note: DeskNote | undefined;
    readonly online: boolean;
    readonly onGenerateAll: (ids: readonly string[]) => void;
}): ReactNode => {
    const { t } = useLingui();
    const items = readiness === undefined ? [] : readinessItems(readiness);
    if (items.length === 0 && note === undefined) return null;
    return (
        <div className="bilda-desk__readiness-block">
            {items.length === 0 ? null : (
                <p className="bilda-desk__readiness" role="note">
                    <Icon name="info" size="sm" />
                    <span className="bilda-desk__readiness-text">
                        {t`Before export:`}{" "}
                        {items.map((item, i) => (
                            <span key={JSON.stringify(item)}>
                                {i > 0 ? " · " : null}
                                <ItemText item={item} />
                            </span>
                        ))}
                    </span>
                    {planned.length === 0 ? null : (
                        <button
                            type="button"
                            className="bilda-desk__generate-it"
                            disabled={!online || busy(note)}
                            onClick={() => onGenerateAll(planned)}
                        >
                            {t`Generate it`}
                        </button>
                    )}
                </p>
            )}
            {note === undefined ? null : <NoteLine note={note} />}
        </div>
    );
};

// Being written (or reading the sources first): the shape of a page,
// filling in, under its step line.
const WritingMiddle = ({
    reading,
}: {
    readonly reading: boolean;
}): ReactNode => {
    const { t } = useLingui();
    return (
        <div className="bilda-desk__writing" aria-busy="true">
            <p className="bilda-desk__state" data-state="writing" role="status">
                <Icon name="sparkle" size="xs" />
                {reading ? t`Reading the sources …` : t`Writing …`}
            </p>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="block" height="var(--space-16)" />
            <Skeleton variant="text" width="85%" />
            <Skeleton variant="text" width="70%" />
        </div>
    );
};

const PlannedMiddle = (): ReactNode => {
    const { t } = useLingui();
    return (
        <p className="bilda-desk__planned-middle">
            {t`Not written yet. Generate it from the brief above.`}
        </p>
    );
};
